import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  FileSpreadsheet,
  Images,
  CheckCircle,
  UploadCloud,
  Download,
  ImagePlus,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../utils/feedback';

const REQUIRED_HEADERS = ['STUDENT_NAME', 'STD_ROMAN'];

const FIELD_HEADER_MAP = [
  { permissionKeys: ['serial_no'], excelHeader: 'SERIAL_NO', sample: '1' },
  { permissionKeys: ['photo_name'], excelHeader: 'PHOTO_NAME', sample: 'aarav.jpg' },
  { permissionKeys: ['student_name'], excelHeader: 'STUDENT_NAME', sample: 'Aarav Kumar', required: true },
  { permissionKeys: ['fathers_name'], excelHeader: 'FATHERS_NAME', sample: 'Raj Kumar' },
  { permissionKeys: ['dob'], excelHeader: 'DATE_OF_BIRTH', sample: '2010-05-10' },
  { permissionKeys: ['std_roman'], excelHeader: 'STD_ROMAN', sample: 'I', required: true },
  { permissionKeys: ['division'], excelHeader: 'DIVISION', sample: 'A' },
  { permissionKeys: ['custom_div'], excelHeader: 'CUSTOM_DIVISION', sample: '' },
  { permissionKeys: ['reg_no'], excelHeader: 'REG_NO', sample: 'REG001' },
  { permissionKeys: ['pen_no', 'appar_id'], excelHeader: 'PEN_NO', sample: 'PEN001' },
  { permissionKeys: ['sat_no'], excelHeader: 'SAT_NO', sample: 'SAT001' },
  { permissionKeys: ['house'], excelHeader: 'HOUSE', sample: 'Red' },
  { permissionKeys: ['address'], excelHeader: 'ADDRESS', sample: 'Chennai' },
  { permissionKeys: ['mobile'], excelHeader: 'MOBILE', sample: '9876543210' },
];

const isFieldEnabled = (field, fieldPermissions) =>
  field.required ||
  (field.permissionKeys || []).some((key) => !!fieldPermissions?.[key]);

const cleanFirestoreData = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    })
  );

const BulkUpload = ({ addStudent }) => {
  const { fieldPermissions, userData, currentUser, profile, schoolId } = useAuth();

  const resolvedSchoolId =
    schoolId ||
    userData?.schoolId ||
    userData?.school_id ||
    currentUser?.schoolId ||
    currentUser?.school_id ||
    profile?.schoolId ||
    profile?.school_id ||
    '';

  const activeExcelHeaders = useMemo(() => {
    return FIELD_HEADER_MAP
      .filter((field) => isFieldEnabled(field, fieldPermissions))
      .map((field) => field.excelHeader);
  }, [fieldPermissions]);

  const [excelData, setExcelData] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const downloadSampleExcel = () => {
    const sampleRow = {};

    FIELD_HEADER_MAP.forEach((field) => {
      if (isFieldEnabled(field, fieldPermissions)) {
        sampleRow[field.excelHeader] = field.sample;
      }
    });

    const worksheet = XLSX.utils.json_to_sheet([sampleRow], {
      header: activeExcelHeaders,
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
    XLSX.writeFile(workbook, 'student_bulk_upload_sample.xlsx');
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      if (json.length === 0) {
        showToast({ icon: 'error', title: 'The uploaded Excel file is empty.' });
        return;
      }

      const headers = Object.keys(json[0] || {});
      const missingHeaders = REQUIRED_HEADERS.filter((header) => !headers.includes(header));

      if (missingHeaders.length > 0) {
        showToast({
          icon: 'error',
          title: `Required columns missing: ${missingHeaders.join(', ')}`,
        });
        return;
      }

      const invalidHeaders = headers.filter((header) => !activeExcelHeaders.includes(header));
      if (invalidHeaders.length > 0) {
        showToast({
          icon: 'warning',
          title: `Unexpected columns found: ${invalidHeaders.join(', ')}`,
        });
      }

      setExcelData(json);
      showToast({
        icon: 'success',
        title: `${json.length} records loaded from Excel.`,
      });
    };

    reader.readAsArrayBuffer(file);
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotoFiles(files);
    if (files.length > 0) {
      showToast({
        icon: 'success',
        title: `${files.length} image(s) ready for upload.`,
      });
    }
  };

  const handleBulkUpload = async () => {
    if (excelData.length === 0) {
      showToast({ icon: 'error', title: 'Please upload the Excel file first.' });
      return;
    }

    if (!resolvedSchoolId) {
      console.error('School ID not found from auth context', {
        schoolId,
        userData,
        currentUser,
        profile,
      });
      showToast({
        icon: 'error',
        title: 'School ID not found for the logged-in user.',
      });
      return;
    }

    setUploading(true);
    setProgress({ current: 0, total: excelData.length });
    let matchCount = 0;
    const unmatchedPhotos = [];
    const failedRows = [];

    try {
      for (let index = 0; index < excelData.length; index += 1) {
        const row = excelData[index];
        setProgress({ current: index + 1, total: excelData.length });

        const excelPhotoName = String(row.PHOTO_NAME || '').trim();
        const penValue = String(row.PEN_NO || '').trim();

        const photoFile = photoFiles.find((file) => excelPhotoName === file.name);

        if (excelPhotoName && !photoFile) {
          unmatchedPhotos.push(excelPhotoName);
        }

        const firestoreData = {
          school_id: resolvedSchoolId,
          student_name: String(row.STUDENT_NAME || '').trim(),
          std_roman: String(row.STD_ROMAN || '').trim(),

          serial_no: fieldPermissions?.serial_no ? String(row.SERIAL_NO || '').trim() : '',
          division: fieldPermissions?.division ? String(row.DIVISION || '').trim() : '',
          custom_div: fieldPermissions?.custom_div ? String(row.CUSTOM_DIVISION || '').trim() : '',
          fathers_name: fieldPermissions?.fathers_name ? String(row.FATHERS_NAME || '').trim() : '',
          dob: fieldPermissions?.dob ? String(row.DATE_OF_BIRTH || '').trim() : '',
          reg_no: fieldPermissions?.reg_no ? String(row.REG_NO || '').trim() : '',
          pen_no: fieldPermissions?.pen_no ? penValue : '',
          appar_id: fieldPermissions?.appar_id ? penValue : '',
          sat_no: fieldPermissions?.sat_no ? String(row.SAT_NO || '').trim() : '',
          house: fieldPermissions?.house ? String(row.HOUSE || '').trim() : '',
          address: fieldPermissions?.address ? String(row.ADDRESS || '').trim() : '',
          mobile: fieldPermissions?.mobile ? String(row.MOBILE || '').trim() : '',
          photo_name: fieldPermissions?.photo_name ? (photoFile?.name || excelPhotoName || '') : '',
        };

        const cleanedFirestoreData = cleanFirestoreData(firestoreData);

        try {
          await addStudent(cleanedFirestoreData, photoFile || null);
          matchCount += 1;
        } catch (err) {
          console.error('Bulk upload failed at row:', index + 1);
          console.error('Excel row:', row);
          console.error('Firestore payload:', cleanedFirestoreData);
          console.error('Error:', err);

          failedRows.push({
            rowNumber: index + 1,
            studentName: cleanedFirestoreData.student_name || 'Unknown',
            error: err?.message || 'Upload failed',
          });
        }
      }

      if (matchCount > 0) {
        showToast({
          icon: 'success',
          title: `${matchCount} students saved successfully.`,
        });
      }

      if (unmatchedPhotos.length > 0) {
        showToast({
          icon: 'warning',
          title: `Unmatched photos: ${[...new Set(unmatchedPhotos)].join(', ')}`,
        });
      }

      if (failedRows.length > 0) {
        console.table(failedRows);
        showToast({
          icon: 'warning',
          title: `${failedRows.length} record(s) failed. Check console for details.`,
        });
      }

      if (failedRows.length === 0) {
        setExcelData([]);
        setPhotoFiles([]);
        setProgress({ current: 0, total: 0 });
      }
    } catch (err) {
      showToast({
        icon: 'error',
        title: err?.message || 'Something went wrong during bulk upload.',
      });
    } finally {
      setUploading(false);
    }
  };

  const progressPercent = progress.total
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  return (
    <div className="app-panel bulk-panel mt-3">
      <div className="app-panel-header">
        <h3 className="d-flex align-items-center gap-2">
          <UploadCloud size={22} /> Bulk Import Engine
        </h3>
        <p>Upload Excel data and optional student photos with clear progress feedback.</p>
      </div>

      <div className="mb-4 d-flex flex-wrap align-items-center gap-3">
        <button
          type="button"
          onClick={downloadSampleExcel}
          className="btn btn-outline-primary rounded-pill px-4 py-2 d-inline-flex align-items-center gap-2"
        >
          <Download size={16} />
          Download Sample Excel Format
        </button>

        <p className="text-muted small mb-0">
          Sample Excel fields are generated dynamically from the current school’s enabled Admin configuration.
        </p>
      </div>

      {uploading && (
        <div className="upload-progress-card mb-4">
          <div className="d-flex justify-content-between small mb-2">
            <span>Uploading records...</span>
            <span>{progress.current}/{progress.total}</span>
          </div>
          <div
            className="progress app-progress"
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div className="progress-bar" style={{ width: `${progressPercent}%` }}>
              {progressPercent}%
            </div>
          </div>
        </div>
      )}

      <div className="row g-4">
        <div className="col-lg-6">
          <div className={`upload-dropzone ${excelData.length > 0 ? 'is-complete' : ''}`}>
            <div className="upload-dropzone-header">
              <FileSpreadsheet className={excelData.length > 0 ? 'text-success' : 'text-primary'} />
              {excelData.length > 0 && <CheckCircle size={18} className="text-success" />}
            </div>
            <h4>Step 1: Excel Data</h4>
            <p>Upload the .xlsx file containing student details.</p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelUpload}
              className="form-control app-input"
            />
          </div>
        </div>

        <div className="col-lg-6">
          <div className={`upload-dropzone ${photoFiles.length > 0 ? 'is-complete' : ''} ${uploading ? 'is-disabled' : ''}`}>
            <div className="upload-dropzone-header">
              <ImagePlus className={photoFiles.length > 0 ? 'text-success' : 'text-primary'} />
              {photoFiles.length > 0 && <CheckCircle size={18} className="text-success" />}
            </div>
            <h4>Step 2: Class Photos</h4>
            <p>Select multiple images. PHOTO_NAME is optional.</p>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handlePhotoSelect}
              disabled={uploading || excelData.length === 0}
              className="form-control app-input"
            />
            {photoFiles.length > 0 && (
              <p className="mt-3 text-success small fw-semibold">
                {photoFiles.length} photo(s) selected
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="small text-muted d-flex align-items-center gap-2">
          <Images size={16} />
          {excelData.length} records ready • {photoFiles.length} photos selected
        </div>

        <button
          type="button"
          onClick={handleBulkUpload}
          disabled={uploading || excelData.length === 0}
          className="btn btn-primary app-primary-btn px-4 py-2 rounded-pill"
        >
          {uploading ? (
            <span className="d-inline-flex align-items-center gap-2">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              Uploading Bulk Data...
            </span>
          ) : (
            'Start Bulk Upload'
          )}
        </button>
      </div>
    </div>
  );
};

export default BulkUpload;